// Fila de suporte: filtros (estado, categoria, prioridade, responsável, pesquisa), ordenada pelo prazo mais apertado.
import { useApi } from "@ecliptica/api-client/react";
import { CATEGORIES, PRIORITIES, STATUSES, type Status } from "@ecliptica/contexts-support";
import { SlaBadge, TicketStatusBadge } from "@ecliptica/game-ui";
import { useTranslation } from "@ecliptica/i18n";
import { Badge, Button, Card, EmptyState, Input, NativeSelect, PageHeader, Skeleton, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@ecliptica/ui";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { LifeBuoy, MessageSquareText } from "lucide-react";
import { Link, useSearchParams } from "react-router";
import { formatDate, useDebounced } from "../../lib/format";

const PAGE = 25;
const PRIORITY_VARIANT = { low: "outline", normal: "secondary", high: "warning", urgent: "destructive" } as const;

export function SupportPage() {
  const { t, i18n } = useTranslation();
  const api = useApi();
  const [params, setParams] = useSearchParams();
  const get = (k: string, d = "") => params.get(k) ?? d;
  const qRaw = get("q");
  const q = useDebounced(qRaw);
  const status = get("status", "active");
  const page = Math.max(Number(get("page", "1")), 1);
  const set = (patch: Record<string, string>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    if (!("page" in patch)) next.delete("page");
    setParams(next, { replace: true });
  };
  const filters = {
    status: status === "all" ? undefined : (status as Status | "active"),
    category: get("category") || undefined,
    priority: get("priority") || undefined,
    assignee: get("assignee") || undefined,
    q: q || undefined,
    sort: (get("sort") || undefined) as "sla" | "newest" | "oldest" | "updated" | undefined,
  };
  const list = useQuery({
    queryKey: ["tickets", filters, page],
    queryFn: async () => {
      const res = await api.v1.admin.support.tickets.$get({
        query: { ...(filters as Record<string, string | undefined>), limit: String(PAGE), offset: String((page - 1) * PAGE) },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });
  const pages = Math.max(Math.ceil((list.data?.total ?? 0) / PAGE), 1);

  return (
    <>
      <PageHeader
        title={t("nav.support")}
        description={t("desk.description")}
        actions={
          <Button asChild variant="outline">
            <Link to="/support/canned">
              <MessageSquareText /> {t("desk.canned")}
            </Link>
          </Button>
        }
      />
      <div className="mb-4 flex flex-wrap gap-2">
        <Input type="search" placeholder={t("desk.filters.search")} aria-label={t("desk.filters.search")} value={qRaw} onChange={(e) => set({ q: e.target.value })} className="w-full sm:w-72" />
        <NativeSelect aria-label={t("desk.col.status")} value={status} onChange={(e) => set({ status: e.target.value === "active" ? "" : e.target.value })}>
          <option value="active">{t("desk.filters.active")}</option>
          <option value="all">{t("desk.filters.all")}</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {t(`support.status.${s}`)}
            </option>
          ))}
        </NativeSelect>
        <NativeSelect aria-label={t("desk.filters.allCategories")} value={get("category")} onChange={(e) => set({ category: e.target.value })}>
          <option value="">{t("desk.filters.allCategories")}</option>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {t(`support.category.${c}`)}
            </option>
          ))}
        </NativeSelect>
        <NativeSelect aria-label={t("desk.filters.allPriorities")} value={get("priority")} onChange={(e) => set({ priority: e.target.value })}>
          <option value="">{t("desk.filters.allPriorities")}</option>
          {PRIORITIES.map((p) => (
            <option key={p} value={p}>
              {t(`support.priority.${p}`)}
            </option>
          ))}
        </NativeSelect>
        <NativeSelect aria-label={t("desk.col.assignee")} value={get("assignee")} onChange={(e) => set({ assignee: e.target.value })}>
          <option value="">{t("desk.filters.anyone")}</option>
          <option value="me">{t("desk.filters.me")}</option>
          <option value="none">{t("desk.filters.none")}</option>
        </NativeSelect>
        <NativeSelect aria-label="sort" value={get("sort", "sla")} onChange={(e) => set({ sort: e.target.value === "sla" ? "" : e.target.value })}>
          {(["sla", "newest", "oldest", "updated"] as const).map((s) => (
            <option key={s} value={s}>
              {t(`desk.sort.${s}`)}
            </option>
          ))}
        </NativeSelect>
      </div>
      <Card>
        {list.isPending ? (
          <div className="flex flex-col gap-2 p-4">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : !list.data?.items.length ? (
          <EmptyState icon={<LifeBuoy />} title={t("desk.empty")} className="m-4" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("desk.col.ticket")}</TableHead>
                <TableHead className="hidden lg:table-cell">{t("desk.col.requester")}</TableHead>
                <TableHead>{t("desk.col.status")}</TableHead>
                <TableHead className="hidden md:table-cell">{t("desk.col.priority")}</TableHead>
                <TableHead>{t("desk.col.sla")}</TableHead>
                <TableHead className="hidden md:table-cell">{t("desk.col.assignee")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.data.items.map((tk) => (
                <TableRow key={tk.number}>
                  <TableCell>
                    <Link to={`/support/${tk.number}`} className="block hover:text-gold-bright">
                      <span className="block truncate font-semibold">{tk.subject}</span>
                      <span className="text-xs text-muted-foreground">
                        {tk.number} · {t(`support.category.${tk.category}`)} · {formatDate(tk.createdAt, i18n.language)}
                      </span>
                    </Link>
                  </TableCell>
                  <TableCell className="hidden text-sm lg:table-cell">
                    {tk.userName ?? tk.contactName ?? "—"}
                    <span className="block text-xs text-muted-foreground">
                      {tk.contactEmail}
                      {!tk.userId && ` · ${t("desk.noAccount")}`}
                    </span>
                  </TableCell>
                  <TableCell>
                    <TicketStatusBadge status={tk.status as Status} />
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <Badge variant={PRIORITY_VARIANT[tk.priority as keyof typeof PRIORITY_VARIANT]}>{t(`support.priority.${tk.priority}`)}</Badge>
                  </TableCell>
                  <TableCell>
                    <SlaBadge state={tk.sla.worst} />
                  </TableCell>
                  <TableCell className="hidden text-sm md:table-cell">{tk.assigneeName ?? "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
      <div className="mt-4 flex items-center justify-end gap-2 text-sm text-muted-foreground">
        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => set({ page: String(page - 1) })}>
          {t("users.prev")}
        </Button>
        <span>{t("users.page", { page, pages })}</span>
        <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => set({ page: String(page + 1) })}>
          {t("users.next")}
        </Button>
      </div>
    </>
  );
}
